<template>
  <div v-if="pricesLoadedFirstTime" class="ticker-container">
    <v-slide-group class="ticker-slide-group" center-active v-model="slideGroupModel">
      <v-slide-item
        v-for="metal in ['Gold', 'Silver', 'Platinum', 'Palladium']"
        :key="metal"
        v-slot="{ active, toggle }"
      >
        <div
          class="pl-2 pr-2"
          :class="active ? 'active' : 'inactive'"
          @click="toggle"
        >
          <span v-if="spotPrices[metal]" class="text-no-wrap spot-price-font"
          ><strong>{{ metalLabel(metal) }}</strong>
            {{ spotPrices[metal].bid | toCurrency }} /
            {{ spotPrices[metal].ask | toCurrency }}
            <template v-if="$vuetify.breakpoint.mdAndUp">
              <span
                v-if="canShowDelta(spotPrices[metal]) && spotPriceDelta(spotPrices[metal]) > 0"
                class="green--text"
              ><v-icon color="green">mdi-trending-up</v-icon>{{
                  spotPriceDelta(spotPrices[metal]) | toCurrency
                }}</span
              >
              <span
                v-else-if="canShowDelta(spotPrices[metal]) && spotPriceDelta(spotPrices[metal]) < 0"
                class="red--text"
              ><v-icon color="red">mdi-trending-down</v-icon>{{
                  spotPriceDelta(spotPrices[metal]) | toCurrency
                }}</span
              >
            </template>
          </span>
        </div>
      </v-slide-item>
      <v-slide-item class="pl-2 pr-2" v-slot="{ active, toggle }">
        <div
          class="pl-2 pr-2"
          :class="active ? 'active' : 'inactive'"
          @click="toggle"
        >
          <span class="text-caption">{{
              pricesAsOf(spotPrices)
            }}</span>
        </div>
      </v-slide-item>
    </v-slide-group>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

const SPOT_PRICE_UPDATE_INTERVAL = 5000

export default {
  name: 'Ticker',
  props: ['provider'],
  data() {
    return {
      online: true,
      error: false,
      slideGroupModel: null,
      spotPriceInterval: null,
      spotPrices: {},
      pricesLoadedFirstTime: false,
    }
  },
  methods: {
    canShowDelta(price) {
      return (
        price != null &&
        price.mid != null &&
        price.open != null &&
        !Number.isNaN(Number(price.mid)) &&
        !Number.isNaN(Number(price.open))
      )
    },
    spotPriceDelta(price) {
      return Number(price.mid) - Number(price.open)
    },
    metalLabel(metal) {
      if ([`xs`, `sm`].indexOf(this.$vuetify.breakpoint.name) === -1)
        return metal
      switch (metal) {
        case `Gold`:
          return `AU`
        case `Silver`:
          return `AG`
        case `Platinum`:
          return `PT`
        case `Palladium`:
          return `PD`
      }
    },
    pricesAsOf(spotPrices) {
      const asOfDate = _.get(spotPrices, `Gold.createdAt`)
      if (!asOfDate) {
        if (!this.error) this.error = true
        return ``
      } else {
        if (this.error) this.error = false
      }
      const d = new Date(asOfDate)
      return `(Prices as of: ${d.toLocaleString()})`
    },
    async spotPriceUpdater() {
      if (this.online) {
        try {
          this.spotPrices = await this.$store.dispatch('getCurrentSpotPrice', {
            provider: this.provider,
          })
          if (Object.keys(this.spotPrices).length > 0) {
             this.pricesLoadedFirstTime = true
          }
        } catch (e) {
          //fail silently but retry
        }
        this.spotPriceInterval = setTimeout(() => {
          this.spotPriceUpdater()
        }, SPOT_PRICE_UPDATE_INTERVAL)
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.spotPriceInterval)
  },
  computed: {
    ...mapGetters(['getAccessToken']),
  },
  created() {
    if (window._TICKER === undefined) {
      window._TICKER = this
    }
    window._VMA.$on('AUTH_FAILED', () => {
      clearInterval(this.spotPriceInterval)
      this.online = false
    })
    window._VMA.$on('LOGOUT', () => {
      clearInterval(this.spotPriceInterval)
      this.online = false
    })
  },
  async mounted() {
    try {
       this.spotPrices = await this.$store.dispatch('getCurrentSpotPrice', {
        provider: this.provider,
      })
      if (Object.keys(this.spotPrices).length > 0) {
         this.pricesLoadedFirstTime = true
      }
    } catch(e) {
      // Initial fetch failed, flag remains false
    }
    this.spotPriceInterval = setTimeout(() => {
      this.spotPriceUpdater()
    }, SPOT_PRICE_UPDATE_INTERVAL)
  },
}
</script>

<style scoped>
.ticker-container {
  /* Assuming the blue background is applied by the parent */
  min-height: 24px;
  height: auto;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 100%;
}

.ticker-slide-group {
  width: 100%;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* Ensure inner elements align vertically */
.ticker-slide-group ::v-deep .v-slide-group__wrapper {
  display: flex;
  align-items: center;
  height: 100%;
  touch-action: pan-x;
}

.ticker-slide-group ::v-deep .v-slide-group__content {
  display: flex;
  align-items: center;
  height: 100%;
  white-space: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  justify-content: center; /* Center items horizontally when they don't overflow */
}

.ticker-slide-group ::v-deep .v-slide-group__content::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Opera */
}

.ticker-container .pl-2 {
  padding-left: 0.5rem !important;
}
.ticker-container .pr-2 {
  padding-right: 0.5rem !important;
}

@media (max-width: 600px) {
  .ticker-container {
    min-height: 32px;
  }
  
  .ticker-container .pl-2,
  .ticker-container .pr-2 {
    padding-left: 0.25rem !important;
    padding-right: 0.25rem !important;
  }
}
</style>
